import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    horizontalListSortingStrategy,
    rectSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {apiFetch, isAuthError} from '../api';
import {useToast} from './Toast';
import Spinner from './Spinner';
import Checklist from './Checklist';
import NewChecklistModal from './NewChecklistModal';
import GroupSidebar from './GroupSidebar';
import {GridIcon, ColumnsIcon} from './icons';
import TemplateContainer from './TemplateContainer';
import AllItemsView from './AllItemsView';
import ScheduledView from './ScheduledView';

const DATE_VIEWS = ['scheduled'];
const SPECIAL_VIEWS = ['all', 'starred', ...DATE_VIEWS];

function SortableChecklist({checklist, isDragEnabled, ...props}) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: checklist.id, disabled: !isDragEnabled});
    const {role, 'aria-disabled': _ariaDisabled, ...safeAttrs} = attributes;
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <div ref={setNodeRef} style={style} className="sortable-checklist-wrapper" id={`checklist-${checklist.id}`} {...safeAttrs}>
            <Checklist data={checklist} dragListeners={isDragEnabled ? listeners : null} isDragEnabled={isDragEnabled} {...props} />
        </div>
    );
}

export default function ChecklistContainer({sidebarOpen, onViewLabelChange}) {
    const showToast = useToast();
    const onError = useCallback((err) => { if (!isAuthError(err)) showToast(err.message); }, [showToast]);
    const [loading, setLoading] = useState(true);
    const [checklistData, setChecklistData] = useState([]);
    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalTemplates, setModalTemplates] = useState([]);
    const [focusedId, setFocusedId] = useState(null);
    const [innerView, setInnerView] = useState('checklists');
    const [allItemsMode, setAllItemsMode] = useState(false);
    const [starredItemsMode, setStarredItemsMode] = useState(false);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('checklistViewMode') || 'columns');

    const toggleViewMode = useCallback((mode) => {
        setViewMode(mode);
        localStorage.setItem('checklistViewMode', mode);
    }, []);

    useEffect(() => {
        if (!focusedId) return;
        const handleKeyDown = (e) => { if (e.key === 'Escape') setFocusedId(null); };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [focusedId]);

    const focusedChecklist = useMemo(() => focusedId ? checklistData.find(c => c.id === focusedId) : null, [focusedId, checklistData]);

    const focusChecklist = useCallback((id) => setFocusedId(id), []);
    const unfocusChecklist = useCallback(() => setFocusedId(null), []);

    const dataRef = useRef(checklistData);
    useEffect(() => { dataRef.current = checklistData; }, [checklistData]);

    useEffect(() => {
        const controller = new AbortController();
        Promise.all([
            apiFetch('/checklists', {signal: controller.signal}).then(res => res.json()),
            apiFetch('/checklist-groups', {signal: controller.signal}).then(res => res.json()),
        ]).then(([checklists, grps]) => {
            setChecklistData(checklists);
            setGroups(grps);
            setLoading(false);
        }).catch(err => {
            if (err.name !== 'AbortError') {
                onError(err);
                setLoading(false);
            }
        });
        return () => controller.abort();
    }, [onError]);

    const isReadOnlyView = selectedGroupId === 'starred' || DATE_VIEWS.includes(selectedGroupId);
    const isReorderable = !isReadOnlyView && selectedGroupId !== 'all';

    const visibleChecklists = useMemo(() => {
        if (selectedGroupId === 'all') return checklistData;
        if (selectedGroupId === 'starred') {
            return checklistData
                .filter(c => c.items.some(i => i.starred))
                .map(c => ({...c, items: c.items.filter(i => i.starred)}));
        }
        if (selectedGroupId === 'scheduled') return checklistData;
        if (selectedGroupId === null) return checklistData.filter(c => c.groupId == null);
        return checklistData.filter(c => c.groupId === selectedGroupId);
    }, [checklistData, selectedGroupId]);

    const allGroupedItems = useMemo(() => {
        if (selectedGroupId !== 'all' || !allItemsMode) return [];
        const groupMap = {};
        for (const g of groups) {
            groupMap[g.id] = g.name;
        }
        const buckets = {};
        for (const c of checklistData) {
            if (c.items.length === 0) continue;
            const groupName = c.groupId ? (groupMap[c.groupId] || 'Default') : 'Default';
            if (!buckets[groupName]) buckets[groupName] = [];
            buckets[groupName].push({
                checklistId: c.id,
                checklistTitle: c.title,
                items: c.items,
            });
        }
        return Object.entries(buckets).map(([groupName, checklists]) => ({groupName, checklists}));
    }, [checklistData, groups, selectedGroupId, allItemsMode]);

    const starredGroupedItems = useMemo(() => {
        if (selectedGroupId !== 'starred' || !starredItemsMode) return [];
        const groupMap = {};
        for (const g of groups) {
            groupMap[g.id] = g.name;
        }
        const buckets = {};
        for (const c of checklistData) {
            const starredItems = c.items.filter(i => i.starred);
            if (starredItems.length === 0) continue;
            const groupName = c.groupId ? (groupMap[c.groupId] || 'Default') : 'Default';
            if (!buckets[groupName]) buckets[groupName] = [];
            buckets[groupName].push({
                checklistId: c.id,
                checklistTitle: c.title,
                items: starredItems,
            });
        }
        return Object.entries(buckets).map(([groupName, checklists]) => ({groupName, checklists}));
    }, [checklistData, groups, selectedGroupId, starredItemsMode]);

    const putChecklist = useCallback(async (checklistId, updated) => {
        try {
            const res = await apiFetch(`/checklists/${checklistId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(updated),
            });
            const saved = await res.json();
            setChecklistData(prev => prev.map(c => c.id === checklistId ? saved : c));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const deleteChecklist = useCallback(async (id) => {
        try {
            await apiFetch(`/checklists/${id}`, {method: 'DELETE'});
            setChecklistData(prev => prev.filter(c => c.id !== id));
            setFocusedId(prev => prev === id ? null : prev);
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const updateTitle = useCallback((checklistId, title) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        putChecklist(checklistId, {...checklist, title});
    }, [putChecklist]);

    const updateItem = useCallback(async (checklistId, itemId, title) => {
        try {
            const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({title}),
            });
            const updated = await res.json();
            setChecklistData(prev => prev.map(c =>
                c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
            ));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const deleteItem = useCallback(async (checklistId, itemId) => {
        try {
            await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {method: 'DELETE'});
            setChecklistData(prev => prev.map(c =>
                c.id === checklistId ? {...c, items: c.items.filter(i => i.id !== itemId)} : c
            ));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const addItem = useCallback(async (checklistId, title) => {
        try {
            const res = await apiFetch(`/checklists/${checklistId}/items`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({title}),
            });
            const newItem = await res.json();
            setChecklistData(prev => prev.map(c =>
                c.id === checklistId ? {...c, items: [newItem, ...c.items]} : c
            ));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const starItem = useCallback(async (checklistId, itemId) => {
        try {
            const checklist = dataRef.current.find(c => c.id === checklistId);
            const item = checklist.items.find(i => i.id === itemId);
            const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({starred: !item.starred}),
            });
            const updated = await res.json();
            setChecklistData(prev => prev.map(c =>
                c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
            ));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const setDueDate = useCallback(async (checklistId, itemId, dueDate) => {
        try {
            const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({dueDate}),
            });
            const updated = await res.json();
            setChecklistData(prev => prev.map(c =>
                c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
            ));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const reorderItems = useCallback((checklistId, movedItemId, afterId, newItems) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const updated = {...checklist, items: newItems, sortMode: 'custom'};
        setChecklistData(prev => prev.map(c => c.id === checklistId ? updated : c));
        apiFetch(`/checklists/${checklistId}/items/${movedItemId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({after: afterId}),
        }).catch(err => onError(err));
        if (checklist.sortMode !== 'custom') {
            apiFetch(`/checklists/${checklistId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({...checklist, sortMode: 'custom'}),
            }).catch(err => onError(err));
        }
    }, [onError]);

    const reorderChecklists = useCallback((movedId, afterId, reorderedGroup) => {
        setChecklistData(prev => {
            const groupIds = new Set(reorderedGroup.map(c => c.id));
            const result = [];
            let ri = 0;
            for (const c of prev) {
                result.push(groupIds.has(c.id) ? reorderedGroup[ri++] : c);
            }
            return result;
        });
        apiFetch(`/checklists/${movedId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({after: afterId, reorder: true}),
        }).catch(err => onError(err));
    }, [onError]);

    const toggleSinkChecked = useCallback((checklistId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const newVal = checklist.sinkCheckedItems === false;
        putChecklist(checklistId, {...checklist, sinkCheckedItems: newVal});
    }, [putChecklist]);

    const toggleHideChecked = useCallback((checklistId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const newVal = !checklist.hideCheckedItems;
        putChecklist(checklistId, {...checklist, hideCheckedItems: newVal});
    }, [putChecklist]);

    const toggleSortMode = useCallback((checklistId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        if (checklist.sortMode === 'custom') {
            putChecklist(checklistId, {...checklist, sortMode: 'created_at'});
        } else {
            const sorted = [...checklist.items].sort((a, b) => a.position - b.position);
            putChecklist(checklistId, {...checklist, items: sorted, sortMode: 'custom'});
        }
    }, [putChecklist]);

    const toggleItem = useCallback(async (checklistId, itemId) => {
        try {
            const checklist = dataRef.current.find(c => c.id === checklistId);
            const item = checklist.items.find(i => i.id === itemId);
            const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({checked: !item.checked}),
            });
            const updated = await res.json();
            setChecklistData(prev => prev.map(c =>
                c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
            ));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const addNewChecklist = useCallback(async (title, groupId) => {
        try {
            const body = {title, items: []};
            if (groupId) {
                body.groupId = groupId;
            } else if (selectedGroupId !== 'all') {
                body.groupId = selectedGroupId;
            }
            const res = await apiFetch('/checklists', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(body),
            });
            const created = await res.json();
            setChecklistData(prev => [...prev, created]);
            setFocusedId(created.id);
        } catch (err) {
            onError(err);
        }
    }, [selectedGroupId, onError]);

    const openNewListModal = useCallback(async () => {
        try {
            const res = await apiFetch('/checklist-templates');
            const templates = await res.json();
            setModalTemplates(templates);
            setShowModal(true);
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const handleModalConfirm = useCallback(async (title, templateId, groupId) => {
        setShowModal(false);
        if (templateId) {
            try {
                const res = await apiFetch(`/checklist-templates/${templateId}/spawn`, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({name: title, groupId}),
                });
                const created = await res.json();
                setChecklistData(prev => [...prev, created]);
                setFocusedId(created.id);
            } catch (err) {
                onError(err);
            }
        } else {
            addNewChecklist(title, groupId);
        }
    }, [addNewChecklist, onError]);

    const renameGroup = useCallback(async (id, name) => {
        try {
            await apiFetch(`/checklist-groups/${id}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({name}),
            });
            setGroups(prev => prev.map(g => g.id === id ? {...g, name} : g));
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const createGroup = useCallback(async (name) => {
        try {
            const res = await apiFetch('/checklist-groups', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({name}),
            });
            const group = await res.json();
            setGroups(prev => [...prev, group]);
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const deleteGroup = useCallback(async (id) => {
        try {
            await apiFetch(`/checklist-groups/${id}`, {method: 'DELETE'});
            setGroups(prev => prev.filter(g => g.id !== id));
            setSelectedGroupId(prev => prev === id ? 'all' : prev);
        } catch (err) {
            onError(err);
        }
    }, [onError]);

    const selectGroup = useCallback((id) => { setInnerView('checklists'); setSelectedGroupId(id); }, []);
    const showTemplates = useCallback(() => { setInnerView('templates'); setSelectedGroupId(undefined); }, []);
    const closeModal = useCallback(() => setShowModal(false), []);
    const handleSpawn = useCallback((checklist) => {
        setChecklistData(prev => [...prev, checklist]);
        setInnerView('checklists');
        setSelectedGroupId(checklist.groupId || null);
        setTimeout(() => {
            const el = document.getElementById(`checklist-${checklist.id}`);
            if (el) el.scrollIntoView({behavior: 'smooth', block: 'center'});
        }, 100);
    }, []);

    useEffect(() => {
        if (innerView === 'templates') { onViewLabelChange('Templates'); return; }
        if (selectedGroupId === 'all') { onViewLabelChange('All'); return; }
        if (selectedGroupId === 'starred') { onViewLabelChange('Starred'); return; }
        if (selectedGroupId === 'scheduled') { onViewLabelChange('Scheduled'); return; }
        if (selectedGroupId === null) { onViewLabelChange(groups.length > 0 ? 'Default' : 'All'); return; }
        const group = groups.find(g => g.id === selectedGroupId);
        onViewLabelChange(group ? group.name : 'All');
    }, [selectedGroupId, groups, innerView, onViewLabelChange]);

    const checklistSensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 8}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
    );

    function handleChecklistDragEnd(event) {
        const {active, over} = event;
        if (!over || active.id === over.id) return;
        const oldIndex = visibleChecklists.findIndex(c => c.id === active.id);
        const newIndex = visibleChecklists.findIndex(c => c.id === over.id);
        const reordered = arrayMove(visibleChecklists, oldIndex, newIndex);
        const afterId = newIndex > 0 ? reordered[newIndex - 1].id : null;
        reorderChecklists(active.id, afterId, reordered);
    }

    return (
        <div className="checklist-layout">
            <GroupSidebar
                groups={groups}
                selectedGroupId={selectedGroupId}
                checklistData={checklistData}
                onSelect={selectGroup}
                onAddGroup={createGroup}
                onRenameGroup={renameGroup}
                onDeleteGroup={deleteGroup}
                onTemplates={showTemplates}
                isTemplatesActive={innerView === 'templates'}
                hasDueDateItems={checklistData.some(c => c.items.some(i => i.dueDate))}
                sidebarOpen={sidebarOpen}
            />
            {loading ? (
                <Spinner size={28} className="spinner-center" />
            ) : innerView === 'templates' ? (
                <TemplateContainer groups={groups} onSpawn={handleSpawn} />
            ) : (
                <div className="checklist-container-root">
                    {focusedChecklist ? (
                        <>
                            <div className="focus-breadcrumb-bar">
                                <button className="focus-breadcrumb-link" onClick={unfocusChecklist}>
                                    {(() => {
                                        const group = groups.find(g => g.id === focusedChecklist.groupId);
                                        return group ? group.name : (SPECIAL_VIEWS.includes(selectedGroupId) ? selectedGroupId.charAt(0).toUpperCase() + selectedGroupId.slice(1) : 'Default');
                                    })()}
                                </button>
                                <span className="focus-breadcrumb-sep">/</span>
                                <span className="focus-breadcrumb-current">{focusedChecklist.title || 'Untitled'}</span>
                            </div>
                            <div className="checklists-container">
                                <Checklist
                                    data={focusedChecklist}
                                    onDelete={deleteChecklist}
                                    onAddItem={addItem}
                                    onToggleItem={toggleItem}
                                    onDeleteItem={deleteItem}
                                    onUpdateTitle={updateTitle}
                                    onUpdateItem={updateItem}
                                    onReorderItems={reorderItems}
                                    onToggleSortMode={toggleSortMode}
                                    onToggleSinkChecked={toggleSinkChecked}
                                    onToggleHideChecked={toggleHideChecked}
                                    onStarItem={starItem}
                                    onSetDueDateItem={setDueDate}
                                    isFocused
                                    onFocus={focusChecklist}
                                    onUnfocus={unfocusChecklist}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                    {!isReadOnlyView && selectedGroupId !== 'all' && (
                        <div className="template-toolbar">
                            <button className="add-checklist-button" onClick={openNewListModal}>
                                + New List
                            </button>
                            <div className="view-toggle">
                                <button
                                    className={`view-toggle-btn${viewMode === 'grid' ? ' active' : ''}`}
                                    onClick={() => toggleViewMode('grid')}
                                    title="Grid view"
                                >
                                    <GridIcon />
                                </button>
                                <button
                                    className={`view-toggle-btn${viewMode === 'columns' ? ' active' : ''}`}
                                    onClick={() => toggleViewMode('columns')}
                                    title="Column view"
                                >
                                    <ColumnsIcon />
                                </button>
                            </div>
                        </div>
                    )}
                    {showModal ? (
                        <NewChecklistModal onConfirm={handleModalConfirm} onCancel={closeModal} templates={modalTemplates} groups={groups} defaultGroupId={selectedGroupId && !SPECIAL_VIEWS.includes(selectedGroupId) ? selectedGroupId : null}/>
                    ) : null}
                    {selectedGroupId === 'all' && (
                        <div className="timeline-toggle-header">
                            <div className="timeline-segmented-control">
                                <button className={`timeline-segment${!allItemsMode ? ' timeline-segment--active' : ''}`} onClick={() => setAllItemsMode(false)}>By Checklist</button>
                                <button className={`timeline-segment${allItemsMode ? ' timeline-segment--active' : ''}`} onClick={() => setAllItemsMode(true)}>By Item</button>
                            </div>
                        </div>
                    )}
                    {selectedGroupId === 'starred' && (
                        <div className="timeline-toggle-header">
                            <div className="timeline-segmented-control">
                                <button className={`timeline-segment${!starredItemsMode ? ' timeline-segment--active' : ''}`} onClick={() => setStarredItemsMode(false)}>By Checklist</button>
                                <button className={`timeline-segment${starredItemsMode ? ' timeline-segment--active' : ''}`} onClick={() => setStarredItemsMode(true)}>By Item</button>
                            </div>
                        </div>
                    )}
                    {selectedGroupId === 'scheduled' ? (
                        <ScheduledView
                            checklistData={checklistData}
                            onToggleItem={toggleItem}
                            onDeleteItem={deleteItem}
                            onUpdateItem={updateItem}
                            onStarItem={starItem}
                            onSetDueDateItem={setDueDate}
                        />
                    ) : selectedGroupId === 'all' && allItemsMode ? (
                        <AllItemsView
                            groupedData={allGroupedItems}
                            onToggleItem={toggleItem}
                            onDeleteItem={deleteItem}
                            onUpdateItem={updateItem}
                            onStarItem={starItem}
                            onSetDueDateItem={setDueDate}
                        />
                    ) : selectedGroupId === 'starred' && starredItemsMode ? (
                        <AllItemsView
                            groupedData={starredGroupedItems}
                            onToggleItem={toggleItem}
                            onDeleteItem={deleteItem}
                            onUpdateItem={updateItem}
                            onStarItem={starItem}
                            onSetDueDateItem={setDueDate}
                        />
                    ) : (
                    <DndContext sensors={checklistSensors} collisionDetection={closestCenter} onDragEnd={handleChecklistDragEnd}>
                        <SortableContext items={visibleChecklists.map(c => c.id)} strategy={viewMode === 'grid' ? rectSortingStrategy : horizontalListSortingStrategy}>
                            <div className={`checklists-container${viewMode === 'grid' ? ' templates-grid' : ''}${focusedId !== null ? ' checklists-container--hidden' : ''}`}>
                                {visibleChecklists.length === 0 && (
                                    <div className="empty-state">
                                        <p className="empty-state-text">
                                            {selectedGroupId === 'starred'
                                                ? 'Zero stars. Your tasks have the charisma of a wet napkin.'
                                                : 'This group is emptier than your fridge on a Sunday night.'}
                                        </p>
                                    </div>
                                )}
                                {visibleChecklists.map(checklist => (
                                    <SortableChecklist
                                        key={checklist.id}
                                        checklist={checklist}
                                        isDragEnabled={isReorderable}
                                        onDelete={deleteChecklist}
                                        onAddItem={addItem}
                                        onToggleItem={toggleItem}
                                        onDeleteItem={deleteItem}
                                        onUpdateTitle={updateTitle}
                                        onUpdateItem={updateItem}
                                        onReorderItems={reorderItems}
                                        onToggleSortMode={toggleSortMode}
                                        onToggleSinkChecked={toggleSinkChecked}
                                        onToggleHideChecked={toggleHideChecked}
                                        onStarItem={starItem}
                                        onSetDueDateItem={setDueDate}
                                        readOnly={isReadOnlyView}
                                        isFocused={focusedId === checklist.id}
                                        onFocus={focusChecklist}
                                        onUnfocus={unfocusChecklist}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                    )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
